import {useCallback} from "react";

import {Errors} from "@khanacademy/wonder-stuff-core";
import {useGql} from "@khan/data-gql";
import {useGuideState} from "@khan/guide-state";
import {KAError} from "@khan/logging";

import type {
    AnswerKey,
    BlooketQuestion,
    QuizContent,
} from "../blooket/types.ts";
import {ANSWER_KEYS} from "../blooket/utils.ts";
import {GET_BLOOKET_QUESTION_SET} from "../graphql/get-blooket-question-set-query.ts";

export const useGetBlooketQuestionSet =
    (): (() => Promise<QuizContent | void>) => {
        const gqlFetch = useGql();
        const threadId = useGuideState("threadId");

        const fetchQuery = useCallback(async () => {
            try {
                if (!threadId) {
                    return;
                }
                const result = await gqlFetch(GET_BLOOKET_QUESTION_SET, {
                    variables: {
                        threadId,
                    },
                });

                const {blooketQuestionSet} = result;

                if (!blooketQuestionSet) {
                    return;
                }

                const questions: BlooketQuestion[] = [];

                for (const question of blooketQuestionSet.questions) {
                    const answers: Record<AnswerKey, string> = {
                        A: "",
                        B: "",
                        C: "",
                        D: "",
                    };

                    // there is no way a legitimate user can save
                    // a question without a correct answer
                    //
                    // this default value is to appease the compiler
                    let correctAnswer: AnswerKey = "A";

                    question.answers.forEach((answer, idx) => {
                        if (answer.isCorrect) {
                            correctAnswer = ANSWER_KEYS[idx];
                        }

                        answers[ANSWER_KEYS[idx]] = answer.text;
                    });

                    questions.push({
                        prompt: question.text,
                        answerKey: correctAnswer,
                        answers: answers,
                    });
                }

                return {
                    title: blooketQuestionSet.title,
                    description: blooketQuestionSet.description,
                    questions: questions,
                };
            } catch (error: any) {
                if (error instanceof KAError) {
                    throw error;
                }

                throw new KAError(
                    "Failed to get question set",
                    Errors.Internal,
                );
            }
        }, [gqlFetch, threadId]);

        return fetchQuery;
    };
